/**
 * Enum definitions for the Fire Defense System
 * These enums provide consistent values across the application
 */

import NotificationPreference from '../generated/src/model/NotificationPreference';

// System mode enum - whether the AI (AUTO) or a human operator (MANUAL) controls activations
export const SYSTEM_MODE = {
  MANUAL: 'MANUAL',
  AUTO: 'AUTO',
};

// Shared mode enum - overall activation lifecycle state
export const SHARED_MODE = {
  UNINITIALIZED: 'UNINITIALIZED',
  PRIMING: 'PRIMING',
  DEACTIVATING: 'DEACTIVATING',
  ACTIVATING: 'ACTIVATING',
  ON: 'ON',
  OFF: 'OFF',
  ERROR: 'ERROR',
};

// Special mode enum - active sub-state modifier
export const SPECIAL_MODE = {
  NONE: 'NONE',
  PRESOAKING: 'PRESOAKING',
  COLLABORATIVE_SOAK: 'COLLABORATIVE_SOAK',
  NON_EMERGENCY_COOLDOWN: 'NON_EMERGENCY_COOLDOWN',
};

// Connection status enum
export const CONNECTION_STATUS = {
  CONNECTED: 'connected',
  CONNECTING: 'connecting',
  DISCONNECTED: 'disconnected',
  ERROR: 'error'
};

// Component status enum - represents the operational state of sensors and relays
export const COMPONENT_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  STANDBY: 'standby'
};

// Health status enum - represents the health/condition of a component
export const HEALTH_STATUS = {
  GOOD: 'good',
  WARNING: 'warning',
  ERROR: 'error',
  UNKNOWN: 'unknown'
};

// Sensor type constants
export const SENSOR_TYPES = {
  EMBER: 'ember',
  SMOKE: 'smoke',
  IR: 'ir',
  SIGNAL: 'signal',
  TEMPERATURE: 'temperature',
  PRESSURE: 'pressure',
  FLOW: 'flow',
  WATER: 'water'
};

// Map of Sensor ID's to simple naming schemes
export const SENSOR_ID_MAP = {
      [SENSOR_TYPES.EMBER]: 'EmberSensor',
      [SENSOR_TYPES.SMOKE]: 'SmokeSensor',
      [SENSOR_TYPES.IR]: 'IRCameraSensor',
      [SENSOR_TYPES.WATER]: 'WaterPumpRelay',  // or whatever water sensor you have
      [SENSOR_TYPES.SIGNAL]: 'SignalSensor'     // or whatever signal sensor you have
    };

// CSS class names for status-based styling of tiles and text
export const CSS_STATUS_CLASS = {
  READY: 'status-ready',
  ALERT: 'status-alert',
  ERROR: 'status-error',
  DISCONNECTED: 'status-disconnected',
};

// Action type constants
export const ACTION_TYPES = {
  START: 'start',
  STOP: 'stop'
};

/** Warning states with status and message combined */
export const WARNING_STATES = {
  NORMAL: {
    status: 'normal',
    message: 'System Normal: No Threats Detected'
  },
  WARNING: {
    status: 'warning',
    message: 'Warning: Threat Detected - Verify Safety of Your Home'
  },
  ACTIVE: {
    status: 'active',
    message: 'Active: Defense Engaged'
  },
  PRESOAKING: {
    status: 'presoaking',
    message: 'Presoaking: Presoaking In Progress'
  },
  ERROR: {
    status: 'error',
    message: 'Error: Component Failure Detected'
  },
  SYNC_WARNING: {
    status: 'sync_warning',
    message: 'Warning: System Data Out of Sync'
  },
  WATER_EMPTY: {
    status: 'error',
    message: 'Error: Water Tank Empty - Refill Immediately'
  },
  WATER_LOW: {
    status: 'warning',
    message: 'Warning: Water Level Low - Refill Recommended'
  },
  SIGNAL_LOW: {
    status: 'warning',
    message: 'Warning: Signal Strength Low'
  },
  COLLABORATIVE: {
    status: 'collaborative',
    message: 'Active: Collaborative Defense Engaged'
  }
};

/**
 * Notification topics, derived from the generated NotificationPreference.TopicEnum
 * so this file fails to compile/reference if the API contract changes.
 */
export const NOTIFICATION_TOPICS = {
  FIRE_CONTROLLER_ACTIVATION: NotificationPreference.TopicEnum.fire_controller_activation,
  FIRE_CONTROLLER_CONNECTION_LOSS: NotificationPreference.TopicEnum.fire_controller_connection_loss,
  NEIGHBOR_SYSTEM_ACTIVATION: NotificationPreference.TopicEnum.neighbor_system_activation,
};

// Notification channel keys, in display order
export const NOTIFICATION_CHANNELS = ['emailEnabled', 'smsEnabled', 'pushEnabled'];

// Display labels for each notification channel column
export const CHANNEL_LABELS = {
  emailEnabled: 'Email',
  smsEnabled: 'SMS',
  pushEnabled: 'Push',
};